import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabaseClient';

export interface GeneralSettings {
  theme: 'light' | 'dark' | 'auto';
  language: string;
  dateFormat: string;
  timeFormat: '24h' | '12h';
  currency: string;
  firstDayOfWeek: number;
  autoSave: boolean;
  autoSaveInterval: number;
  compactMode: boolean;
  showTooltips: boolean;
  animationsEnabled: boolean;
  soundsEnabled: boolean;
  defaultView: string;
  itemsPerPage: number;
  exportFormat: string;
}

export interface SecuritySettings {
  twoFactorEnabled: boolean;
  sessionTimeout: number;
  passwordExpiry: number;
  ipWhitelist: string[];
  loginNotifications: boolean;
  apiAccess: boolean;
  apiKey: string;
  webhookUrl: string;
  dataEncryption: boolean;
  auditLog: boolean;
}

export const useSettings = () => {
  const [generalSettings, setGeneralSettings] = useState<GeneralSettings>({
    theme: 'dark',
    language: 'pl',
    dateFormat: 'DD.MM.YYYY',
    timeFormat: '24h',
    currency: 'PLN',
    firstDayOfWeek: 1,
    autoSave: true,
    autoSaveInterval: 30,
    compactMode: false,
    showTooltips: true,
    animationsEnabled: true,
    soundsEnabled: false,
    defaultView: 'dashboard',
    itemsPerPage: 25,
    exportFormat: 'xlsx'
  });

  const [securitySettings, setSecuritySettings] = useState<SecuritySettings>({
    twoFactorEnabled: false,
    sessionTimeout: 60,
    passwordExpiry: 90,
    ipWhitelist: [],
    loginNotifications: true,
    apiAccess: false,
    apiKey: '',
    webhookUrl: '',
    dataEncryption: true,
    auditLog: true
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Stały UUID dla domyślnego użytkownika
  const userId = '00000000-0000-0000-0000-000000000001';

  // Pobierz ustawienia z bazy danych
  const fetchSettings = async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('user_settings')
        .select('preferences')
        .eq('user_id', userId)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      if (data?.preferences) {
        // Rozdziel preferencje na ogólne i bezpieczeństwa
        const prefs = data.preferences;
        if (prefs.general) {
          setGeneralSettings(prev => ({ ...prev, ...prefs.general }));
        }
        if (prefs.security) {
          setSecuritySettings(prev => ({ ...prev, ...prefs.security }));
        }
      } else if (!data) {
        // Tylko jeśli nie ma rekordu, utwórz domyślne ustawienia
        // Użyj insert zamiast saveAllSettings żeby uniknąć konfliktu
        await supabase
          .from('user_settings')
          .insert({
            user_id: userId,
            preferences: {
              general: generalSettings,
              security: securitySettings,
              lastUpdated: new Date().toISOString()
            }
          });
      }

      // Załaduj też z localStorage jako backup
      loadLocalSettings();
    } catch (err: any) {
      console.error('Error fetching settings:', err);
      setError(err.message);
      // W przypadku błędu użyj lokalnych ustawień
      loadLocalSettings();
    } finally {
      setLoading(false);
    }
  };

  // Zapisz wszystkie ustawienia
  const saveAllSettings = async () => {
    try {
      const preferences = {
        general: generalSettings,
        security: securitySettings,
        lastUpdated: new Date().toISOString()
      };

      // Zapisz do localStorage
      localStorage.setItem('plexisystem_general_settings', JSON.stringify(generalSettings));
      localStorage.setItem('plexisystem_security_settings', JSON.stringify(securitySettings));

      // Sprawdź czy rekord istnieje
      const { data: existing } = await supabase
        .from('user_settings')
        .select('id')
        .eq('user_id', userId)
        .maybeSingle();

      let error;
      
      if (existing) {
        // Rekord istnieje - użyj update
        const result = await supabase
          .from('user_settings')
          .update({ preferences })
          .eq('user_id', userId);
        error = result.error;
      } else {
        // Rekord nie istnieje - użyj insert
        const result = await supabase
          .from('user_settings')
          .insert({
            user_id: userId,
            preferences
          });
        error = result.error;
      }

      if (error) throw error;

      return { success: true };
    } catch (err: any) {
      console.error('Error saving settings:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Zapisz ustawienia ogólne
  const saveGeneralSettings = async (settings: GeneralSettings) => {
    setGeneralSettings(settings);
    localStorage.setItem('plexisystem_general_settings', JSON.stringify(settings));
    
    try {
      const { data: currentData } = await supabase
        .from('user_settings')
        .select('preferences')
        .eq('user_id', userId)
        .maybeSingle();

      const updatedPreferences = {
        ...(currentData?.preferences || {}),
        general: settings,
        lastUpdated: new Date().toISOString()
      };

      let error;
      
      if (currentData) {
        // Rekord istnieje - użyj update
        const result = await supabase
          .from('user_settings')
          .update({ preferences: updatedPreferences })
          .eq('user_id', userId);
        error = result.error;
      } else {
        // Rekord nie istnieje - użyj insert
        const result = await supabase
          .from('user_settings')
          .insert({
            user_id: userId,
            preferences: updatedPreferences
          });
        error = result.error;
      }

      if (error) throw error;

      // Zastosuj niektóre ustawienia od razu
      applySettings(settings);

      return { success: true };
    } catch (err: any) {
      console.error('Error saving general settings:', err);
      return { success: false, error: err.message };
    }
  };

  // Zapisz ustawienia bezpieczeństwa
  const saveSecuritySettings = async (settings: SecuritySettings) => {
    setSecuritySettings(settings);
    localStorage.setItem('plexisystem_security_settings', JSON.stringify(settings));
    
    try {
      const { data: currentData } = await supabase
        .from('user_settings')
        .select('preferences')
        .eq('user_id', userId)
        .maybeSingle();

      const updatedPreferences = {
        ...(currentData?.preferences || {}),
        security: settings,
        lastUpdated: new Date().toISOString()
      };

      let error;
      
      if (currentData) {
        // Rekord istnieje - użyj update
        const result = await supabase
          .from('user_settings')
          .update({ preferences: updatedPreferences })
          .eq('user_id', userId);
        error = result.error;
      } else {
        // Rekord nie istnieje - użyj insert
        const result = await supabase
          .from('user_settings')
          .insert({
            user_id: userId,
            preferences: updatedPreferences
          });
        error = result.error;
      }

      if (error) throw error;

      return { success: true };
    } catch (err: any) {
      console.error('Error saving security settings:', err);
      return { success: false, error: err.message };
    }
  };

  // Załaduj ustawienia z localStorage
  const loadLocalSettings = () => {
    try {
      const savedGeneral = localStorage.getItem('plexisystem_general_settings');
      const savedSecurity = localStorage.getItem('plexisystem_security_settings');
      
      if (savedGeneral) {
        const parsed = JSON.parse(savedGeneral);
        setGeneralSettings(prev => ({ ...prev, ...parsed }));
      }
      
      if (savedSecurity) {
        const parsed = JSON.parse(savedSecurity);
        setSecuritySettings(prev => ({ ...prev, ...parsed }));
      }
    } catch (err) {
      console.error('Error loading local settings:', err);
    }
  };

  // Zastosuj ustawienia (np. motyw, język)
  const applySettings = (settings: GeneralSettings) => {
    // Zastosuj motyw
    if (settings.theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else if (settings.theme === 'light') {
      document.documentElement.classList.remove('dark');
    } else {
      // Auto - użyj preferencji systemowych
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      if (prefersDark) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    }

    // Zastosuj animacje
    if (!settings.animationsEnabled) {
      document.documentElement.style.setProperty('--animation-duration', '0ms');
    } else {
      document.documentElement.style.removeProperty('--animation-duration');
    }

    // Możesz dodać więcej zastosowań ustawień tutaj
  };

  // Generuj nowy klucz API
  const generateApiKey = () => {
    const prefix = 'pk_live_';
    const randomString = Array.from(crypto.getRandomValues(new Uint8Array(24)))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
    return prefix + randomString;
  };

  // Hook effect do załadowania ustawień przy starcie
  useEffect(() => {
    fetchSettings();
  }, []);

  // Zastosuj ustawienia przy zmianie
  useEffect(() => {
    applySettings(generalSettings);
  }, [generalSettings]);

  // Nasłuchuj na zmiany w innych kartach
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'plexisystem_general_settings' && e.newValue) {
        try {
          const newSettings = JSON.parse(e.newValue);
          setGeneralSettings(newSettings);
          applySettings(newSettings);
        } catch (err) {
          console.error('Error parsing storage change:', err);
        }
      }
      
      if (e.key === 'plexisystem_security_settings' && e.newValue) {
        try {
          const newSettings = JSON.parse(e.newValue);
          setSecuritySettings(newSettings);
        } catch (err) {
          console.error('Error parsing storage change:', err);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  return {
    generalSettings,
    securitySettings,
    loading,
    error,
    saveGeneralSettings,
    saveSecuritySettings,
    generateApiKey,
    refreshSettings: fetchSettings
  };
};
